'use client'

import { useState, useEffect } from 'react'
import Link from 'next/link'
import { formatDate, formatEventDateRange } from '@/lib/timed-ui/formatting'
import { useFormatPrice } from '@/lib/timed-ui/use-format-price'
import Badge from '../common/Badge'
import ShareButton from '../common/ShareButton'
import type { TimedUIEvent } from '@/lib/timed-ui/adapters'
import { useTheme } from '@/components/public/branding/ThemeProvider'
import { getTemplateConfig } from '@/lib/templates/template-config'


type SalePhase =
  | { type: 'presale_upcoming'; date: Date }
  | { type: 'presale_active'; endDate: Date | null }
  | { type: 'public_upcoming'; date: Date }
  | { type: 'on_sale' }
  | { type: 'sale_ended' }
  | null

function getSalePhase(event: TimedUIEvent): SalePhase {
  const now = new Date()

  if (event.saleEndDate && now > new Date(event.saleEndDate)) {
    return { type: 'sale_ended' }
  }

  if (event.presaleEnabled && event.presaleStartDate) {
    const presaleStart = new Date(event.presaleStartDate)
    if (now < presaleStart) {
      return { type: 'presale_upcoming', date: presaleStart }
    }
    if (event.presaleEndDate) {
      const presaleEnd = new Date(event.presaleEndDate)
      if (now < presaleEnd) {
        return { type: 'presale_active', endDate: presaleEnd }
      }
    }
  }

  if (event.publicSaleStartDate) {
    const publicStart = new Date(event.publicSaleStartDate)
    if (now < publicStart) {
      return { type: 'public_upcoming', date: publicStart }
    }
  }

  return null
}

function formatCountdown(target: Date): string {
  const now = Date.now()
  const diff = target.getTime() - now
  if (diff <= 0) return ''

  const days = Math.floor(diff / (1000 * 60 * 60 * 24))
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60))
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((diff % (1000 * 60)) / 1000)

  if (days > 0) return `${days}d ${hours}h ${minutes}m`
  if (hours > 0) return `${hours}h ${minutes}m ${seconds}s`
  return `${minutes}m ${seconds}s`
}

function PresaleCountdown({ event }: { event: TimedUIEvent }) {
  // Defer phase computation until after mount. Server-side and the
  // first client render both produce phase=null, so SSR and hydration
  // agree (no React #425 text-content mismatch). Client picks up the
  // real phase on the first useEffect tick.
  const [phase, setPhase] = useState<SalePhase>(null)
  const [countdown, setCountdown] = useState('')

  useEffect(() => {
    const update = () => {
      const p = getSalePhase(event)
      setPhase(p)
      if (p?.type === 'presale_upcoming') {
        setCountdown(formatCountdown(p.date))
      } else if (p?.type === 'presale_active' && p.endDate) {
        setCountdown(formatCountdown(p.endDate))
      } else if (p?.type === 'public_upcoming') {
        setCountdown(formatCountdown(p.date))
      } else {
        setCountdown('')
      }
    }

    update()
    const interval = setInterval(update, 1000)
    return () => clearInterval(interval)
  }, [event])

  if (!phase) return null

  if (phase.type === 'presale_upcoming') {
    return (
      <div className="mt-3 pt-3 border-t border-surface-border">
        <div className="flex items-center gap-2 mb-1.5">
          <svg className="w-3.5 h-3.5 text-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs font-semibold text-text-primary">Presale starts in</span>
        </div>
        <div className="text-sm font-mono font-bold text-primary tracking-wide">
          {countdown}
        </div>
        <div className="text-[11px] text-text-tertiary mt-0.5">
          {formatDate(phase.date.toISOString(), 'MMM dd, yyyy h:mm a', event.timezone)}
        </div>
      </div>
    )
  }

  if (phase.type === 'presale_active') {
    return (
      <div className="mt-3 pt-3 border-t border-surface-border">
        <div className="flex items-center gap-2">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-success opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-success" />
          </span>
          <span className="text-xs font-semibold text-success">Presale Active</span>
          {countdown && (
            <span className="text-[11px] text-text-tertiary ml-auto">Ends in {countdown}</span>
          )}
        </div>
      </div>
    )
  }

  if (phase.type === 'public_upcoming') {
    return (
      <div className="mt-3 pt-3 border-t border-surface-border">
        <div className="flex items-center gap-2 mb-1.5">
          <svg className="w-3.5 h-3.5 text-text-secondary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <span className="text-xs font-semibold text-text-primary">On sale in</span>
        </div>
        <div className="text-sm font-mono font-bold text-text-primary tracking-wide">
          {countdown}
        </div>
        <div className="text-[11px] text-text-tertiary mt-0.5">
          {formatDate(phase.date.toISOString(), 'MMM dd, yyyy h:mm a', event.timezone)}
        </div>
      </div>
    )
  }

  if (phase.type === 'sale_ended') {
    return (
      <div className="mt-3 pt-3 border-t border-surface-border">
        <span className="text-xs font-medium text-text-tertiary">Sales ended</span>
      </div>
    )
  }

  return null
}

interface EventCardProps {
  event: TimedUIEvent
}

const EventCard = ({ event }: EventCardProps) => {
  const formatPrice = useFormatPrice()
  const { branding } = useTheme()
  const template = getTemplateConfig(branding?.layout)
  const cardConfig = template.eventCard
  // branding.publicSite.features.enableSocialSharing — absent/true shows the
  // share button (current behavior); explicit false hides it.
  const enableSocialSharing =
    (branding?.publicSite as { features?: { enableSocialSharing?: boolean } } | undefined)
      ?.features?.enableSocialSharing !== false

  const eventImage = event.imageUrl || event.smallPic || event.largePic || ''
  const eventTitle = event.title || event.name || 'Untitled Event'
  const isSoldOut = event.status === 'SOLD_OUT'
  const isCancelled = event.status === 'CANCELLED'
  const href = `/events/${event.slug || event.id}`

  // Defer phase to post-mount so SSR and first client render agree on
  // the rendered output (server clock != client clock can flip the
  // phase across a sale boundary, producing React #425).
  const [salePhase, setSalePhase] = useState<SalePhase>(null)
  useEffect(() => {
    setSalePhase(getSalePhase(event))
  }, [event])
  const hasPresaleBadge = salePhase?.type === 'presale_upcoming' || salePhase?.type === 'presale_active'

  // Lowest non-addon ticket price for the "From $X" indicator
  const pricedTickets = (event.ticketTypes || []).filter(t => t.allInPrice > 0 && !t.isAddon)
  const minPrice = pricedTickets.length > 0 ? Math.min(...pricedTickets.map(t => t.allInPrice)) : 0

  // Template-aware layout classes
  const layoutClasses = cardConfig.layout === 'horizontal' ? 'flex flex-row' : 'block'

  // Template-aware hover effects
  const hoverClasses =
    cardConfig.hoverEffect === 'lift' ? 'hover:-translate-y-1 hover:shadow-lg' :
    cardConfig.hoverEffect === 'scale' ? 'hover:scale-105' :
    ''

  // Template-aware image aspect ratio
  const imageRatioClass =
    cardConfig.imageRatio === 'square' ? 'aspect-square' :
    cardConfig.imageRatio === '4:3' ? 'aspect-[4/3]' :
    cardConfig.imageRatio === '3:2' ? 'aspect-[3/2]' :
    'aspect-video'

  // Template-aware image container width for horizontal layout
  const imageWidthClass = cardConfig.layout === 'horizontal' ? 'w-1/3 shrink-0' : 'w-full'

  return (
    <Link
      href={href}
      className={`event-card bg-surface border border-surface-border rounded-xl overflow-hidden cursor-pointer group shadow-card ${hoverClasses} hover:border-surface-border hover:shadow-card-hover transition-all duration-200 animate-fade-in ${layoutClasses} ${isSoldOut || isCancelled ? 'opacity-60' : ''}`}
    >
      <div className={`relative ${imageWidthClass} ${imageRatioClass} overflow-hidden`}>
        {eventImage ? (
          <>
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={eventImage}
              alt=""
              aria-hidden="true"
              className="absolute inset-0 w-full h-full object-cover scale-110 blur-xl opacity-50"
            />
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src={eventImage}
              alt={eventTitle}
              className="relative z-10 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
              loading="lazy"
            />
            <div className="absolute inset-0 z-20 bg-gradient-to-t from-black/30 via-transparent to-transparent" />
          </>
        ) : (
          <div className="w-full h-full bg-surface-tertiary relative">
            <div className="absolute inset-0 flex items-center justify-center">
              <svg className="w-16 h-16 text-text-tertiary opacity-30" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z" />
              </svg>
            </div>
          </div>
        )}
        {isSoldOut && (
          <div className="absolute top-3 left-3 z-30">
            <Badge variant="error" size="md">Sold Out</Badge>
          </div>
        )}
        {isCancelled && (
          <div className="absolute top-3 left-3 z-30">
            <Badge variant="neutral" size="md">Cancelled</Badge>
          </div>
        )}
        {hasPresaleBadge && !isSoldOut && !isCancelled && (
          <div className="absolute top-3 left-3 z-30">
            <Badge variant={salePhase?.type === 'presale_active' ? 'success' : 'primary'} size="md">
              {salePhase?.type === 'presale_active' ? 'Presale' : 'Coming Soon'}
            </Badge>
          </div>
        )}
        {event.isFeatured && !isSoldOut && !isCancelled && !hasPresaleBadge && (
          <div className="absolute top-3 left-3 z-30">
            <Badge variant="primary" size="md">Featured</Badge>
          </div>
        )}
      </div>

      <div className={`p-4 ${cardConfig.layout === 'horizontal' ? 'flex-1' : ''}`}>
        <h3 className="text-base font-heading font-semibold text-text-primary mb-2 line-clamp-2 leading-snug group-hover:text-primary transition-colors">
          {eventTitle}
        </h3>

        {/* Conditional description for Classic template */}
        {cardConfig.showDescription && event.description && (
          <p className="text-sm text-text-secondary line-clamp-2 mb-2">
            {event.description}
          </p>
        )}

        <div className="space-y-1.5 text-sm">
          {event.startDate && (
            <div className="flex items-center text-text-secondary">
              <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span>{formatEventDateRange(event.startDate, event.endDate, false, event.timezone)}</span>
            </div>
          )}

          {event.venue && (
            <div className="flex items-center text-text-secondary">
              <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0 text-text-tertiary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{event.venue.name}</span>
            </div>
          )}

          {event.venueCity && (
            <div className="flex items-center text-text-tertiary">
              <svg className="w-3.5 h-3.5 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
              <span className="truncate">{event.venueCity}</span>
            </div>
          )}
        </div>

        <PresaleCountdown event={event} />

        {!salePhase && (
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-surface-border gap-2" onClick={e => e.preventDefault()}>
            {minPrice > 0 ? (
              <span className="text-sm font-semibold text-text-primary">
                From {formatPrice(minPrice)}
              </span>
            ) : <span />}
            {enableSocialSharing && (
              <ShareButton
                url={typeof window !== 'undefined' ? `${window.location.origin}${href}` : href}
                title={eventTitle}
                text={`Check out ${eventTitle}!`}
                variant="button"
              />
            )}
          </div>
        )}
      </div>
    </Link>
  )
}

export default EventCard
